import { ApiActionHeaderAndParameters, isSecretValue } from '../../types';

/**
 * Extracts secret IDs from API action step headers and parameters
 *
 * @param headers - API action step headers
 * @param parameters - API action step parameters
 * @returns Array of unique secret IDs used in the step
 */
export function extractSecretIdsFromApiActionStep({
  headers,
  parameters
}: {
  headers: ApiActionHeaderAndParameters | undefined;
  parameters: ApiActionHeaderAndParameters | null | undefined;
}): string[] {
  const headerSecretIds = Object.values(headers || {})
    .filter(isSecretValue)
    .map((header) => header.secretId);

  const parameterSecretIds = Object.values(parameters || {})
    .filter(isSecretValue)
    .map((param) => param.secretId);

  const allSecretIds = [...headerSecretIds, ...parameterSecretIds];

  // Return deduplicated array
  return Array.from(new Set(allSecretIds));
}

/**
 * Extracts secret IDs from multiple API action steps
 *
 * @param steps - Array of API action steps with headers and parameters
 * @returns Array of unique secret IDs used across all steps
 */
export function extractSecretIdsFromApiActionSteps<
  T extends {
    headers: ApiActionHeaderAndParameters;
    parameters: ApiActionHeaderAndParameters | null;
  }
>(steps: T[]): string[] {
  const allSecretIds = steps.flatMap((step) =>
    extractSecretIdsFromApiActionStep({
      headers: step.headers,
      parameters: step.parameters
    })
  );

  // Return deduplicated array
  return Array.from(new Set(allSecretIds));
}
